import {
  getAllDeliveryWindowsByBuyerId,
  getBuyerIdFromCurrentOrder,
  getDefaultStockType,
  getFilterFields,
  getIsHideSwitchForSingleStockTypeEnabled,
  getIsStockTypeSeparationEnabled,
  getOrderDeliveryWindows,
  getProductSortingOptions,
  getValidStockTypesByBuyerId,
} from '@app/ducks';
import { areAllDeliveryWindowsOfStockType } from '@app/logic/deliveryWindows';
import { decodeFiltersSearchParam, getValidatedFilters, getValidatedProductSorting } from '@app/logic/filters';
import { ProductFilters } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

import { getQueryParams } from '../getQueryParams';
import { isDefined } from '../is';

export const useCurrentProductFiltersAndSort = () => {
  const filtersFromUrl = decodeFiltersSearchParam(window.location.search) as ProductFilters;
  const urlSorting = getQueryParams(window.location.search).sort;
  const isStockTypeSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const orderDeliveryWindows = useSelector(getOrderDeliveryWindows);
  const buyerIdFromCurrentOrder = useSelector(getBuyerIdFromCurrentOrder);
  const validStockTypeSelector = React.useMemo(() => getValidStockTypesByBuyerId(buyerIdFromCurrentOrder), [buyerIdFromCurrentOrder]);
  const validStockTypes = useSelector(validStockTypeSelector);
  const isHideForSingleStockEnabled = useSelector(getIsHideSwitchForSingleStockTypeEnabled);
  const filterFields = useSelector(getFilterFields);
  const defaultStockType = useSelector(getDefaultStockType);
  const sortingOptions = useSelector(getProductSortingOptions);
  const urlStockType = filtersFromUrl.stockType;
  const hideSwitchForSingleStockType = isHideForSingleStockEnabled && validStockTypes.length === 1;
  const hasOnlyStock = areAllDeliveryWindowsOfStockType(orderDeliveryWindows, 'stock');
  const hasOnlyPreorder = areAllDeliveryWindowsOfStockType(orderDeliveryWindows, 'preorder');
  const deliveryWindowsIdsByBuyerId = useSelector(getAllDeliveryWindowsByBuyerId);
  const deliveryWindowsIds = React.useMemo(() => Object.values(deliveryWindowsIdsByBuyerId).flat(), [deliveryWindowsIdsByBuyerId]);

  const selectionStockType = React.useMemo(
    () =>
      hasOnlyStock ? 'stock'
      : hasOnlyPreorder ? 'preorder'
      : undefined,
    [hasOnlyPreorder, hasOnlyStock],
  );

  const getCurrentStockType = React.useCallback(() => {
    if (hideSwitchForSingleStockType) {
      return validStockTypes[0];
    }

    if (!isStockTypeSeparationEnabled) {
      return undefined;
    }

    if (isDefined(urlStockType) && validStockTypes.includes(urlStockType)) {
      return urlStockType;
    }

    return isDefined(selectionStockType) ? selectionStockType : defaultStockType;
  }, [defaultStockType, hideSwitchForSingleStockType, isStockTypeSeparationEnabled, selectionStockType, urlStockType, validStockTypes]);

  return React.useCallback(() => {
    const filters = getValidatedFilters(filtersFromUrl, filterFields, deliveryWindowsIds);
    const currentSort = getValidatedProductSorting(urlSorting, sortingOptions);

    const stockType = getCurrentStockType();

    return {
      currentFilters: {
        ...filters,
        ...(isDefined(stockType) ? { stockType } : {}),
      },
      currentSort,
    };
  }, [filterFields, filtersFromUrl, getCurrentStockType, sortingOptions, urlSorting, deliveryWindowsIds]);
};
